import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Image,
  List,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Coordinate,
  DurationDistance,
  SpotStat,
  SpotType,
} from "~/generated/graphql.types";
import logo from "~/assets/logo.svg";
import { SpotStatsSummary } from "./BetweenTwoSpotListItems/SpotStat/SpotStatSummary";
import { EditModeContext } from "../..";
import { SpotForRoutingFragment } from "~/routes/_shared/fragments.generated";
import { ShowTutorial } from "./ShowTutorial";
import { SpotsTableModal } from "./SpotsTableModal";
import { convertParamsForSidebar, SpotForSidebar } from "./convertType";
import { ExportCsvButton } from "./ExportCsvButtons";
import { SpotListItem } from "./SpotListItem";
import { BetweenTwoSpotListItems } from "./BetweenTwoSpotListItems";
import { useUpdateCourseMutation } from "./index.generated";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "~/App";
import { CourseAssetIdEditable } from "./AssetId";
import { AssetVersionModal } from "./AssetVersionModal";

export const Sidebar = React.memo(
  ({
    courseId,
    courseName,
    start,
    dest,
    waypoints,
    statsAllSpots,
    statsWithoutOpt,
    isVisibleToAllUserGroups = false,
    onClickSpot,
  }: {
    courseId: string;
    courseName: string;
    start?: SpotForRoutingFragment;
    dest?: SpotForRoutingFragment;
    statsAllSpots: SpotStat[] | undefined;
    statsWithoutOpt: SpotStat[] | undefined;
    isVisibleToAllUserGroups?: boolean;
    waypoints: Array<SpotForRoutingFragment>;
    onClickSpot: (coordinate: Coordinate) => void;
  }) => {
    const {
      editMode,
      spotIdForEdit,
      setSpotIdForEdit,
      switchToContentsMode,
      switchToImageMode,
      switchToRoutingMode,
    } = useContext(EditModeContext);
    const { currentUserId } = useContext(AuthContext);
    const [, updateCourse] = useUpdateCourseMutation();

    const { startSidebar, destSidebar, waypointsSidebar } = useMemo(() => {
      return convertParamsForSidebar({
        start,
        dest,
        waypoints,
        editMode,
      });
    }, [start, dest, waypoints, editMode]);

    const statsSummary = useMemo(() => {
      const stats: DurationDistance[] = [];
      if (statsAllSpots == undefined) return stats;
      statsAllSpots.forEach((value) => {
        stats.push(value.durationDistance);
      });
      return stats;
    }, [statsAllSpots]);

    return (
      <Suspense fallback={<Text>Loading...</Text>}>
        <Box
          p={6}
          bgColor="#0F2446"
          color="white"
          overflowY="scroll"
          h="100vh"
          sx={{
            "&::-webkit-scrollbar": {
              width: 2,
              backgroundColor: "blue.900",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: `blue.400`,
            },
          }}
        >
          <Link to={currentUserId ? `/` : `/admin`}>
            <Image src={logo} marginBottom="1rem" w="24rem" />
          </Link>
          <Flex justifyContent={"space-between"} alignItems="center">
            <Text as="h3" size="md" fontWeight="bold" fontSize={20}>
              {courseName}
            </Text>
            <Flex alignItems="center" flexShrink={0}>
              <Text as="h3" size="md" fontSize={16}>
                コースの公開
              </Text>
              <Switch
                ml={2}
                defaultChecked={isVisibleToAllUserGroups}
                onChange={(event) => {
                  const isReleased = event.target.checked;
                  if (isReleased) {
                    updateCourse({
                      input: { id: courseId, isVisibleToAllUserGroups: true },
                    });
                  } else {
                    updateCourse({
                      input: { id: courseId, isVisibleToAllUserGroups: false },
                    });
                  }
                }}
              />
            </Flex>
          </Flex>
          <CourseAssetIdEditable courseId={courseId} />
          <Box height={3} />
          <AssetVersionModal courseId={courseId} />

          <ButtonGroup colorScheme="blue" w="full" my={4} alignItems="center">
            <Button
              flex={1}
              onClick={switchToRoutingMode}
              variant={editMode === "routing" ? "solid" : "outline"}
            >
              ルート設定
            </Button>
            <Button
              flex={1}
              onClick={switchToContentsMode}
              variant={editMode === "contents" ? "solid" : "outline"}
            >
              コンテンツ設定
            </Button>
            {import.meta.env.VITE_FEATURE_FLAG_ENABLE_ASSET_EDITOR ==
              "true" && (
              <Button
                flex={1}
                onClick={switchToImageMode}
                variant={editMode === "image" ? "solid" : "outline"}
                disabled={false}
              >
                画像設定
              </Button>
            )}
            <SpotsTableModal courseId={courseId} />
          </ButtonGroup>

          <List my={2}>
            {startSidebar ? (
              <SpotListItem
                spot={startSidebar}
                onClick={() => {
                  onClickSpot(startSidebar.coordinate);
                  setSpotIdForEdit(startSidebar.id);
                }}
                isSelected={spotIdForEdit === startSidebar.id}
                resetSpotIdForEdit={() => {
                  setSpotIdForEdit(undefined);
                }}
              />
            ) : null}
            {startSidebar && destSidebar ? (
              <BetweenTwoSpotListItems
                prevSpot={startSidebar}
                nextSpot={waypointsSidebar[0] || destSidebar}
                allSpotsStats={
                  statsAllSpots?.find(
                    (s) => s.statsAfterSpotId === startSidebar.id
                  )?.durationDistance
                }
                withoutOptionalSpotsStats={
                  statsWithoutOpt?.find(
                    (s) => s.statsAfterSpotId === startSidebar.id
                  )?.durationDistance
                }
              />
            ) : null}

            <ShowTutorial
              nextSpotType={
                !start
                  ? SpotType.Start
                  : !dest
                  ? SpotType.Destination
                  : SpotType.Waypoint
              }
              showTutorial={waypoints.length === 0}
            />

            {waypointsSidebar.map((spot, i) => (
              <div key={spot.id}>
                {spot ? (
                  <>
                    <Box>
                      <SpotListItem
                        spot={spot}
                        isSelected={spotIdForEdit === spot.id}
                        onClick={() => {
                          onClickSpot(spot.coordinate);
                          setSpotIdForEdit(spot.id);
                        }}
                        resetSpotIdForEdit={() => {
                          setSpotIdForEdit(undefined);
                        }}
                        isActiveReorderButton={
                          editMode === "contents" &&
                          spot.spotType === SpotType.Narration
                        }
                        isActiveReorderMoveUpButton={
                          i !== 0 &&
                          (!spot.parentSpotId ||
                            spot.parentSpotId ===
                              waypointsSidebar[i - 1]?.parentSpotId)
                        }
                        isActiveReorderMoveDownButton={
                          i !== waypointsSidebar.length - 1 &&
                          (!spot.parentSpotId ||
                            spot.parentSpotId ===
                              waypointsSidebar[i + 1]?.parentSpotId)
                        }
                      />
                    </Box>
                    <BetweenTwoSpotListItems
                      prevSpot={waypointsSidebar[i]}
                      nextSpot={waypointsSidebar[i + 1] || dest}
                      allSpotsStats={
                        statsAllSpots?.find(
                          (s) => s.statsAfterSpotId === waypointsSidebar[i].id
                        )?.durationDistance
                      }
                      withoutOptionalSpotsStats={
                        statsWithoutOpt?.find(
                          (s) => s.statsAfterSpotId === waypointsSidebar[i].id
                        )?.durationDistance
                      }
                    />
                  </>
                ) : null}
              </div>
            ))}
            {destSidebar ? (
              <SpotListItem
                spot={destSidebar}
                isSelected={spotIdForEdit === destSidebar.id}
                onClick={() => {
                  onClickSpot(destSidebar.coordinate);
                  setSpotIdForEdit(destSidebar.id);
                }}
                resetSpotIdForEdit={() => {
                  setSpotIdForEdit(undefined);
                }}
              />
            ) : null}
            {start && dest ? (
              <SpotStatsSummary allSpotStats={statsSummary} />
            ) : null}
          </List>
          <ExportCsvButton courseId={courseId} courseName={courseName} />
          {/* {start && dest ? (
        <CreateHyperlapseOnRouteModal
          courseId={courseId}
          start={start}
          dest={dest}
          waypoints={waypoints}
        />
      ) : null} */}
        </Box>
      </Suspense>
    );
  }
);
